import React, { useState, useEffect } from 'react'
import { Card, Form, InputNumber, Button, Row, Col, message, Spin, Typography, Tooltip } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { httpGet, httpPost, httpPatch } from '../../common/http.service'

const { Text } = Typography

interface CheckInLimitation {
	hourEarlyNotAllowInMinutes: number
	hourLateAllowWithReasonInMinutes: number
	hourLateNotAllowInMinutes: number
}

interface CheckOutLimitation {
	hourEarlyAllowWithReasonInMinutes: number
	hourEarlyNotAllowInMinutes: number
	hourLateNotAllowInMinutes: number
}

const HourLimitationTab: React.FC = () => {
	const [checkInForm] = Form.useForm()
	const [checkOutForm] = Form.useForm()
	const [loadingCheckIn, setLoadingCheckIn] = useState(false)
	const [loadingCheckOut, setLoadingCheckOut] = useState(false)
	const [checkInExists, setCheckInExists] = useState(false)
	const [checkOutExists, setCheckOutExists] = useState(false)
	const [editingCheckIn, setEditingCheckIn] = useState(false)
	const [editingCheckOut, setEditingCheckOut] = useState(false)

	useEffect(() => {
		loadCheckInLimitation()
		loadCheckOutLimitation()
	}, [])

	const loadCheckInLimitation = async () => {
		try {
			setLoadingCheckIn(true)
			const response: any = await httpGet(
				'/api/v1/admin/config-shift/hour-limitation/checkin',
			)
			if (response?.data) {
				checkInForm.setFieldsValue(response.data)
				setCheckInExists(true)
			}
		} catch (error) {
			console.error('Failed to load check-in limitation:', error)
		} finally {
			setLoadingCheckIn(false)
		}
	}

	const loadCheckOutLimitation = async () => {
		try {
			setLoadingCheckOut(true)
			const response: any = await httpGet(
				'/api/v1/admin/config-shift/hour-limitation/checkout',
			)
			if (response?.data) {
				checkOutForm.setFieldsValue(response.data)
				setCheckOutExists(true)
			}
		} catch (error) {
			console.error('Failed to load check-out limitation:', error)
		} finally {
			setLoadingCheckOut(false)
		}
	}

	const handleCheckInSubmit = async (values: CheckInLimitation) => {
		try {
			if (checkInExists) {
				await httpPatch('/api/v1/admin/config-shift/hour-limitation/checkin', values)
				message.success('Check-in limitation updated successfully')
			} else {
				await httpPost('/api/v1/admin/config-shift/hour-limitation/checkin', values)
				message.success('Check-in limitation created successfully')
				setCheckInExists(true)
			}
			setEditingCheckIn(false)
			loadCheckInLimitation()
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to save check-in limitation')
		}
	}

	const handleCheckOutSubmit = async (values: CheckOutLimitation) => {
		try {
			if (checkOutExists) {
				await httpPatch('/api/v1/admin/config-shift/hour-limitation/checkout', values)
				message.success('Check-out limitation updated successfully')
			} else {
				await httpPost('/api/v1/admin/config-shift/hour-limitation/checkout', values)
				message.success('Check-out limitation created successfully')
				setCheckOutExists(true)
			}
			setEditingCheckOut(false)
			loadCheckOutLimitation()
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to save check-out limitation')
		}
	}

	return (
		<div>
			<Row gutter={[24, 24]}>
				{/* Check-In Limitation */}
				<Col xs={24} lg={12}>
					<Card
						title='Check-In Hour Limitation'
						bordered={false}
						style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
						extra={
							!editingCheckIn && (
								<div style={{ display: 'flex', gap: '8px' }}>
									<Tooltip title='Edit button is enabled when configuration exists'>
										<Button
											type='primary'
											icon={<EditOutlined />}
											onClick={() => setEditingCheckIn(true)}
											disabled={!checkInExists}
										>
											Edit
										</Button>
									</Tooltip>
									<Tooltip title="Create button is enabled when there's no configuration">
										<Button
											type='primary'
											onClick={() => setEditingCheckIn(true)}
											disabled={checkInExists}
										>
											Create
										</Button>
									</Tooltip>
								</div>
							)
						}
					>
						<Spin spinning={loadingCheckIn}>
							{!editingCheckIn && checkInExists ? (
								<div>
									<Row gutter={[16, 16]}>
										<Col span={24}>
											<Text strong>Early Check-In Not Allowed (minutes)</Text>
											<div style={{ marginTop: 8 }}>
												<Text style={{ fontSize: 18 }}>
													{checkInForm.getFieldValue(
														'hourEarlyNotAllowInMinutes',
													) || 0}
												</Text>
											</div>
										</Col>
										<Col span={24}>
											<Text strong>
												Late Check-In Allowed with Reason (minutes)
											</Text>
											<div style={{ marginTop: 8 }}>
												<Text style={{ fontSize: 18 }}>
													{checkInForm.getFieldValue(
														'hourLateAllowWithReasonInMinutes',
													) || 0}
												</Text>
											</div>
										</Col>
										<Col span={24}>
											<Text strong>Late Check-In Not Allowed (minutes)</Text>
											<div style={{ marginTop: 8 }}>
												<Text style={{ fontSize: 18 }}>
													{checkInForm.getFieldValue(
														'hourLateNotAllowInMinutes',
													) || 0}
												</Text>
											</div>
										</Col>
									</Row>
								</div>
							) : !editingCheckIn && !checkInExists ? (
								<div style={{ textAlign: 'center', padding: '40px 0' }}>
									<Text type='secondary'>
										No check-in hour limitation configured yet. Click the Create
										button to set up.
									</Text>
								</div>
							) : (
								<Form
									form={checkInForm}
									layout='vertical'
									onFinish={handleCheckInSubmit}
									initialValues={{
										hourEarlyNotAllowInMinutes: 120,
										hourLateAllowWithReasonInMinutes: 15,
										hourLateNotAllowInMinutes: 240,
									}}
								>
									<Form.Item
										name='hourEarlyNotAllowInMinutes'
										label='Early Check-In Not Allowed (minutes)'
										rules={[
											{
												required: true,
												message: 'Please enter early check-in limit',
											},
										]}
										tooltip='How many minutes before scheduled time check-in is not allowed'
									>
										<InputNumber
											min={0}
											style={{ width: '100%' }}
											placeholder='e.g., 120'
										/>
									</Form.Item>

									<Form.Item
										name='hourLateAllowWithReasonInMinutes'
										label='Late Check-In Allowed with Reason (minutes)'
										rules={[
											{
												required: true,
												message:
													'Please enter late check-in with reason limit',
											},
										]}
										tooltip='Maximum minutes late allowed with a reason'
									>
										<InputNumber
											min={0}
											style={{ width: '100%' }}
											placeholder='e.g., 15'
										/>
									</Form.Item>

									<Form.Item
										name='hourLateNotAllowInMinutes'
										label='Late Check-In Not Allowed (minutes)'
										rules={[
											{
												required: true,
												message: 'Please enter late check-in limit',
											},
										]}
										tooltip='Maximum minutes late before check-in is not allowed'
									>
										<InputNumber
											min={0}
											style={{ width: '100%' }}
											placeholder='e.g., 240'
										/>
									</Form.Item>

									<Form.Item>
										<Row gutter={8}>
											<Col span={12}>
												<Button
													block
													onClick={() => setEditingCheckIn(false)}
												>
													Cancel
												</Button>
											</Col>
											<Col span={12}>
												<Button type='primary' htmlType='submit' block>
													{checkInExists ? 'Update' : 'Create'}
												</Button>
											</Col>
										</Row>
									</Form.Item>
								</Form>
							)}
						</Spin>
					</Card>
				</Col>

				{/* Check-Out Limitation */}
				<Col xs={24} lg={12}>
					<Card
						title='Check-Out Hour Limitation'
						bordered={false}
						style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
						extra={
							!editingCheckOut && (
								<div style={{ display: 'flex', gap: '8px' }}>
									<Tooltip title='Edit button is enabled when configuration exists'>
										<Button
											type='primary'
											icon={<EditOutlined />}
											onClick={() => setEditingCheckOut(true)}
											disabled={!checkOutExists}
										>
											Edit
										</Button>
									</Tooltip>
									<Tooltip title="Create button is enabled when there's no configuration">
										<Button
											type='primary'
											onClick={() => setEditingCheckOut(true)}
											disabled={checkOutExists}
										>
											Create
										</Button>
									</Tooltip>
								</div>
							)
						}
					>
						<Spin spinning={loadingCheckOut}>
							{!editingCheckOut && checkOutExists ? (
								<div>
									<Row gutter={[16, 16]}>
										<Col span={24}>
											<Text strong>
												Early Check-Out Allowed with Reason (minutes)
											</Text>
											<div style={{ marginTop: 8 }}>
												<Text style={{ fontSize: 18 }}>
													{checkOutForm.getFieldValue(
														'hourEarlyAllowWithReasonInMinutes',
													) || 0}
												</Text>
											</div>
										</Col>
										<Col span={24}>
											<Text strong>
												Early Check-Out Not Allowed (minutes)
											</Text>
											<div style={{ marginTop: 8 }}>
												<Text style={{ fontSize: 18 }}>
													{checkOutForm.getFieldValue(
														'hourEarlyNotAllowInMinutes',
													) || 0}
												</Text>
											</div>
										</Col>
										<Col span={24}>
											<Text strong>Late Check-Out Not Allowed (minutes)</Text>
											<div style={{ marginTop: 8 }}>
												<Text style={{ fontSize: 18 }}>
													{checkOutForm.getFieldValue(
														'hourLateNotAllowInMinutes',
													) || 0}
												</Text>
											</div>
										</Col>
									</Row>
								</div>
							) : !editingCheckOut && !checkOutExists ? (
								<div style={{ textAlign: 'center', padding: '40px 0' }}>
									<Text type='secondary'>
										No check-out hour limitation configured yet. Click the
										Create button to set up.
									</Text>
								</div>
							) : (
								<Form
									form={checkOutForm}
									layout='vertical'
									onFinish={handleCheckOutSubmit}
									initialValues={{
										hourEarlyAllowWithReasonInMinutes: 15,
										hourEarlyNotAllowInMinutes: 120,
										hourLateNotAllowInMinutes: 180,
									}}
								>
									<Form.Item
										name='hourEarlyAllowWithReasonInMinutes'
										label='Early Check-Out Allowed with Reason (minutes)'
										rules={[
											{
												required: true,
												message:
													'Please enter early check-out with reason limit',
											},
										]}
										tooltip='Maximum minutes early allowed with a reason'
									>
										<InputNumber
											min={0}
											style={{ width: '100%' }}
											placeholder='e.g., 15'
										/>
									</Form.Item>

									<Form.Item
										name='hourEarlyNotAllowInMinutes'
										label='Early Check-Out Not Allowed (minutes)'
										rules={[
											{
												required: true,
												message: 'Please enter early check-out limit',
											},
										]}
										tooltip='How many minutes before scheduled time check-out is not allowed'
									>
										<InputNumber
											min={0}
											style={{ width: '100%' }}
											placeholder='e.g., 120'
										/>
									</Form.Item>

									<Form.Item
										name='hourLateNotAllowInMinutes'
										label='Late Check-Out Not Allowed (minutes)'
										rules={[
											{
												required: true,
												message: 'Please enter late check-out limit',
											},
										]}
										tooltip='Maximum minutes late before check-out is not allowed'
									>
										<InputNumber
											min={0}
											style={{ width: '100%' }}
											placeholder='e.g., 180'
										/>
									</Form.Item>

									<Form.Item>
										<Row gutter={8}>
											<Col span={12}>
												<Button
													block
													onClick={() => setEditingCheckOut(false)}
												>
													Cancel
												</Button>
											</Col>
											<Col span={12}>
												<Button type='primary' htmlType='submit' block>
													{checkOutExists ? 'Update' : 'Create'}
												</Button>
											</Col>
										</Row>
									</Form.Item>
								</Form>
							)}
						</Spin>
					</Card>
				</Col>
			</Row>
		</div>
	)
}

export default HourLimitationTab
